'use client';

import { c as _c } from "react/compiler-runtime";
import 'scrollyfills';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import type { KeyboardEventHandler } from 'react';
import { useEffect, useRef, useState } from 'react';
import { cx } from '../../cx';
import { useMediaState } from '../../hooks/use-media-state';
import { useResizeObserverRef } from '../../hooks/use-resize-observer-ref';
import { uiTestId } from '../../test-ids';
import { BaseButton } from '../button/button';
import { IconButton } from '../button/icon-button';
import { LoadingImage } from '../loading-image/loading-image';
import type { SliderImage } from './image-slider';
import { ImageSliderContainer } from './image-slider-container';
import { ImageSliderItem } from './image-slider-item';
export type ImageSliderLightboxProps = {
  images: SliderImage[];
  initialIndex?: number;
  aspectRatio?: number;
};
const CONTAINER_WIDTH_GAP = 64;
const CONTAINER_PADDING = 32;
const CONTAINER_MAX_WIDTH = 1000;
const CONTAINER_MIN_WIDTH = 200;
const CONTAINER_MIN_HEIGHT = 200;
const IMAGE_CONTAINER_GAP = 32;
const THUMBNAIL_SIZE = 80;
export const ImageSliderLightbox = ({
  images,
  initialIndex,
  aspectRatio = 4 / 3
}: ImageSliderLightboxProps) => {
  const thumbnailRef = useRef<HTMLUListElement>(null);
  const imageContainerRef = useRef<HTMLUListElement | null>(null);
  const performedInitialScroll = useRef(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const isMobile = useMediaState('mobile');
  const scrollToIndex = (index: number, smooth = true) => {
    imageContainerRef.current?.scrollTo({
      left: index * imageContainerRef.current.clientWidth,
      behavior: smooth ? 'smooth' : 'instant'
    });
  };
  const [resizeSliderContainerRef] = useResizeObserverRef(([entry]) => {
    if (entry && imageContainerRef.current && thumbnailRef.current) {
      const containerHeight = Math.max(entry.target.clientHeight - THUMBNAIL_SIZE - CONTAINER_PADDING, CONTAINER_MIN_HEIGHT);
      let containerWidth = entry.target.clientWidth - CONTAINER_WIDTH_GAP * 2;
      containerWidth = Math.min(Math.max(containerWidth, CONTAINER_MIN_WIDTH), CONTAINER_MAX_WIDTH);
      const slideWidth = containerHeight * aspectRatio;
      if (slideWidth <= containerWidth) {
        imageContainerRef.current.style.width = `${slideWidth}px`;
        imageContainerRef.current.style.height = `${containerHeight}px`;
        thumbnailRef.current.style.width = `${slideWidth}px`;
      } else {
        const slideHeight = containerWidth / aspectRatio;
        imageContainerRef.current.style.width = `${containerWidth}px`;
        imageContainerRef.current.style.height = `${slideHeight}px`;
        thumbnailRef.current.style.width = `${containerWidth}px`;
      }
      if (initialIndex && !performedInitialScroll.current) {
        performedInitialScroll.current = true;
        scrollToIndex(initialIndex, false);
      }
    }
  });
  const handleThumbnailClick = (index_0: number) => {
    scrollToIndex(index_0);
  };
  useEffect(() => {
    const imageContainer = imageContainerRef.current;
    const handler = (event: HTMLElementEventMap['scrollend']) => {
      if (!(event.currentTarget instanceof HTMLUListElement)) return;
      const {
        scrollLeft,
        clientWidth
      } = event.currentTarget;
      const index_1 = scrollLeft / clientWidth;

      // Calculate the scroll position for the thumbnails (80px width and 16px gap)
      const left = index_1 * (THUMBNAIL_SIZE + CONTAINER_WIDTH_GAP / 4);
      thumbnailRef.current?.scrollTo({
        left,
        behavior: 'smooth'
      });
      setActiveIndex(Math.round(index_1));
    };
    imageContainer?.addEventListener('scrollend', handler);
    return () => imageContainer?.removeEventListener('scrollend', handler);
  }, []);
  const handleNav = (direction: 'prev' | 'next') => {
    setActiveIndex(oldIndex => {
      const newIndex = direction === 'prev' ? oldIndex - 1 : oldIndex + 1;
      if (newIndex >= 0 && newIndex < images.length) {
        scrollToIndex(newIndex);
        return newIndex;
      }
      return oldIndex;
    });
  };
  const handleKeyDown: KeyboardEventHandler<HTMLElement> = e => {
    switch (e.key) {
      case 'ArrowRight':
        handleNav('next');
        break;
      case 'ArrowLeft':
        handleNav('prev');
        break;
      default:
        break;
    }
  };
  if (isMobile) {
    return <MobileLightBox images={images} initialIndex={initialIndex} />;
  }
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO: fix accessability
    <div className="relative flex size-full flex-row items-center justify-center outline-hidden" data-testid={uiTestId('ImageSlider.lightboxContainer')} ref={ref => {
      ref?.focus();
      return resizeSliderContainerRef(ref);
    }} onKeyDown={handleKeyDown}>
      <div className="mb-10 grid size-fit max-h-full max-w-full grid-rows-[1fr] items-center" style={{
        gap: IMAGE_CONTAINER_GAP
      }}>
        <div className="relative flex h-full flex-row">
          <div className="absolute -left-20 z-40 ml-0 flex h-full items-center">
            <IconButton data-testid={uiTestId('ImageSlider.prevButton')} Icon={CaretLeft} onClick={() => handleNav('prev')} disabled={activeIndex === 0} aria-label="Vorheriges Bild" />
          </div>

          <ImageSliderContainer className="size-52 rounded-xl" // need some pre-set size for safari...
          ref={imageContainerRef} data-testid={uiTestId('ImageSlider.imageSliderContainer')}>
            {images.map((image, index_2) => <ImageSliderItem key={index_2} image={image} imageSizes={image.sizes ?? '(max-width: 768px) 80vw,1000px'} showCopyright />)}
          </ImageSliderContainer>

          <div className="absolute -right-20 flex h-full items-center">
            <IconButton data-testid={uiTestId('ImageSlider.nextButton')} Icon={CaretRight} onClick={() => handleNav('next')} disabled={activeIndex === images.length - 1} aria-label="Nächstes Bild" />
          </div>
        </div>

        {/* Thumbnails */}
        <div className="relative w-0" style={{
          height: THUMBNAIL_SIZE
        }}>
          <ul className="scrollbar-none absolute flex h-fit w-full gap-4 overflow-x-auto" ref={thumbnailRef} data-testid={uiTestId('ImageSlider.thumbnailContainer')}>
            {images.map((image_0, index_3) => <li className="flex-none" key={index_3}>
                <BaseButton key={image_0.url} onClick={() => handleThumbnailClick(index_3)} className={cx('transition-opacity', index_3 !== activeIndex && '!opacity-60')}>
                  <LoadingImage src={image_0.url} alt={image_0.alt || ''} height={THUMBNAIL_SIZE} width={THUMBNAIL_SIZE} className="aspect-square rounded-lg object-cover" sizes="(min-width: 1024px) 640px, (min-width: 1440px) 828px, 384px" />
                </BaseButton>
              </li>)}
          </ul>
        </div>
      </div>
    </div>
  );
};
type MobileLightBoxProps = {
  images: SliderImage[];
  initialIndex?: number;
};
const MobileLightBox = t0 => {
  const $ = _c(10);
  const {
    images,
    initialIndex
  } = t0;
  const containerRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const imagesRef = useRef(t1);
  const performedInitialScroll = useRef(false);
  let t2;
  if ($[1] !== initialIndex) {
    t2 = t3 => {
      const [entry] = t3;
      containerRef.current = entry.target;
      if (initialIndex && initialIndex !== 0 && !performedInitialScroll.current && imagesRef.current[initialIndex]?.clientHeight) {
        performedInitialScroll.current = true;
        imagesRef.current[initialIndex]?.scrollIntoView();
      }
    };
    $[1] = initialIndex;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [containerRefResize] = useResizeObserverRef(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = uiTestId("ImageSlider.mobileLightbox");
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== images) {
    let t5;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = (image, index) => <ImageSliderItem key={index} ref={el => {
        imagesRef.current[index] = el;
      }} image={image} imageSizes={image.sizes ?? "(max-width: 768px) 80vw,1000px"} showCopyright={true} objectFit="contain" liClassName="h-fit rounded-xl overflow-hidden" imageClassName="!relative !h-auto" priority={true} />;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    t4 = images.map(t5);
    $[4] = images;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[7] !== containerRefResize || $[8] !== t4) {
    t5 = <ul className="scrollbar-none flex flex-col gap-5 overflow-y-auto" data-testid={t3} ref={containerRefResize}>{t4}</ul>;
    $[7] = containerRefResize;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};